import {
  CircleGraph,
  formatDate,
  isDateInValidPeriod,
  LinkButton,
  SmallSurveyCard,
  SurveyCard,
  TextBubble,
  theme,
  Tooltip,
  usePath,
} from '@arnold/common';
import { ReactComponent as TeamIcon } from '@arnold/common/src/assets/icons/TeamIcon.svg';
import { REPORT_LINK } from '@arnold/core';
import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationQuery, SurveyStatus } from '../../generated/hooks';
import ee from '../../lib/eventEmitter';
import { getCategoryIcon, goToTalkResults } from '../../lib/helpers';
import { ReportPendingApprovalTooltip } from '../ReportPendingApprovalTooltip';
import { getActiveMessages } from '../SurveysOverview/RowParts/SurveyInfo';
import { DateTooltip } from '../SurveysOverview/RowParts/DateTooltip';
import { DashboardSurvey, getIcon, getLabel, getSubtitle, getTitle, getValue } from './utils';

type ActiveDashboardSurveyCardProps = {
  survey: DashboardSurvey;
  enabledRespondentsCount: number;
  organization: NonNullable<OrganizationQuery['organization']>;
  onResultsClick: () => void;
  hideDetail?: boolean;
  dataIcom?: string;
};

export const ActiveDashboardSurveyCard: FC<ActiveDashboardSurveyCardProps> = ({
  survey,
  enabledRespondentsCount,
  organization,
  onResultsClick,
  hideDetail,
  dataIcom = 'active-survey-card',
}) => {
  const { t } = useTranslation('dashboard');
  const [surveyT] = useTranslation('surveyOverviewRow');
  const { surveyDetailPath } = usePath();

  const graph = (
    <CircleGraph
      value={getValue(survey)}
      title={getTitle(survey, t)}
      dataCy="survey-start-date"
      valueSubTitle={getSubtitle(survey, t)}
      backgroundColor={survey.status === SurveyStatus.Notstarted ? theme.colors.emotionWarning.background : undefined}
      icon={getIcon(survey)}
      hideValueInGraph={!!survey.public}
    />
  );
  const reportAccess = survey.topLevelReport;
  const inLicenceInterval = isDateInValidPeriod(new Date(survey.sendAt), organization.productVersionHistory!);
  const notStartedReasonMessage = getActiveMessages(
    {
      isSequential: false,
      notstartedReason: [survey.notstartedReason!],
      inLicenceInterval,
      organization,
    },
    surveyT,
  );

  return (
    <SurveyCard
      dataIcom={dataIcom}
      header={
        <DateTooltip
          startsAt={survey.sendAt}
          endsAt={survey.endsAt}
          reminders={survey.reminders}
          dontDisplayDateLength={true}
        >
          {survey.topic?.name || survey.topicGroup?.name || ''}
        </DateTooltip>
      }
      headerIcon={getCategoryIcon(
        !survey.topicGroup?.categoryCode,
        survey.topicGroup?.categoryCode,
        survey.public ?? undefined,
      )}
      upperTitle={
        <DateTooltip
          startsAt={survey.sendAt}
          endsAt={survey.endsAt}
          reminders={survey.reminders}
          dontDisplayDateLength={true}
        >
          <TextBubble
            color={
              [SurveyStatus.Ongoing, SurveyStatus.Notstarted, SurveyStatus.Activating].includes(survey.status)
                ? undefined
                : theme.colors.emotionInformative.hover
            }
            backgroundColor={
              [SurveyStatus.Ongoing, SurveyStatus.Notstarted, SurveyStatus.Activating].includes(survey.status)
                ? undefined
                : theme.colors.emotionInformative.background
            }
            label={getLabel(survey, t)}
          />
        </DateTooltip>
      }
      graph={
        survey.status === SurveyStatus.Notstarted ? (
          <Tooltip title={notStartedReasonMessage.join('<hr />')}>{graph}</Tooltip>
        ) : (
          graph
        )
      }
      lowerTitle={
        <DateTooltip
          startsAt={survey.sendAt}
          endsAt={survey.endsAt}
          reminders={survey.reminders}
          dontDisplayDateLength={true}
        >
          <TextBubble
            padding={`6px ${theme.spacing.d}`}
            label={
              survey.public ? (
                t('startAtDate', { date: formatDate(survey.sendAt) })
              ) : survey.status === SurveyStatus.Ongoing ? (
                `${formatDate(survey.sendAt)} - ${survey.endsAt ? formatDate(survey.endsAt) : '-'}`
              ) : (
                <Participants>
                  <TeamIcon />
                  {survey.whitelistedRespondentsLength ? '' : `${t('wholeOrganization')} | `}
                  {t('participants', {
                    count: survey.whitelistedRespondentsLength
                      ? survey.whitelistedRespondentsLength
                      : enabledRespondentsCount,
                    postProcess: 'interval',
                  })}
                </Participants>
              )
            }
          />
        </DateTooltip>
      }
      lowerTitleMobile={
        survey.status === SurveyStatus.Ongoing && !survey.public ? (
          <div>
            <span
              style={{
                fontSize: theme.typography.body.small.regular.fontSize,
                lineHeight: '120%',
                color: theme.colors.text.secondary,
              }}
            >
              {t('responseRate')}
            </span>
            <strong
              style={{
                padding: `0 ${theme.spacing.d}`,
                fontSize: theme.typography.heading.small.default.fontSize,
                lineHeight: '140%',
              }}
            >{`${getValue(survey)}`}</strong>
            <span style={{ fontSize: theme.typography.body.small.regular.fontSize, lineHeight: '200%' }}>
              {getSubtitle(survey, t)}
            </span>
          </div>
        ) : (
          <span>
            {t('participants', {
              count: survey.public
                ? survey.resultsInfo?.answered
                : survey.whitelistedRespondentsLength
                  ? survey.whitelistedRespondentsLength
                  : enabledRespondentsCount,
              postProcess: 'interval',
            })}
          </span>
        )
      }
      footer={
        <Footer>
          {survey.public && survey.accessKey && (
            <LinkButton
              onClick={() => {
                ee.emitModalSharePublicSurvey(survey.accessKey!, survey.topic?.name || '');
              }}
              data-icom={'btn-public-survey-share'}
            >
              {t('share')}
            </LinkButton>
          )}
          {survey.status === SurveyStatus.Ongoing && reportAccess && (
            <ReportPendingApprovalTooltip isPending={survey.isReportPendingApproval}>
              <LinkButton
                disabled={survey.isReportPendingApproval}
                onClick={() => {
                  if (survey.isReportPendingApproval) {
                    return;
                  }
                  goToTalkResults(REPORT_LINK.REPORT, reportAccess.accessKey);
                  onResultsClick();
                }}
                data-icom={'btn-active-survey-card-results'}
              >
                {t('results')}
              </LinkButton>
            </ReportPendingApprovalTooltip>
          )}
          {!hideDetail && (
            <LinkButton onClick={() => surveyDetailPath.toFunc(survey.id)} data-cy="btn-survey-detail">
              {t('detail')}
            </LinkButton>
          )}
        </Footer>
      }
    />
  );
};

type CompletedDashboardSurveyCardProps = {
  survey: DashboardSurvey;
  onResultsClick: () => void;
  hideDetail?: boolean;
  dataIcom?: string;
};

export const CompletedDashboardSurveyCard: FC<CompletedDashboardSurveyCardProps> = ({
  survey,
  onResultsClick,
  hideDetail,
  dataIcom = 'completed-survey-card',
}) => {
  const { t } = useTranslation('dashboard');
  const { surveyDetailPath } = usePath();
  const reportAccess = survey.topLevelReport;

  return (
    <SmallSurveyCard
      dataIcom={dataIcom}
      header={
        <DateTooltip
          startsAt={survey.sendAt}
          endsAt={survey.endsAt}
          reminders={survey.reminders}
          dontDisplayDateLength={true}
        >
          {survey.topic?.name || survey.topicGroup?.name || ''}
        </DateTooltip>
      }
      headerIcon={getCategoryIcon(!survey.topicGroup?.categoryCode, survey.topicGroup?.categoryCode)}
      results={
        <Rate>
          <strong style={{ paddingRight: theme.spacing.d }}>{getValue(survey)}</strong>
          <span style={{ fontSize: theme.typography.body.small.regular.fontSize, lineHeight: '120%' }}>
            {getSubtitle(survey, t)}
          </span>
        </Rate>
      }
      footer={
        <>
          {reportAccess && (
            <ReportPendingApprovalTooltip isPending={survey.isReportPendingApproval}>
              <LinkButton
                margin={'0'}
                disabled={survey.isReportPendingApproval}
                onClick={() => {
                  if (survey.isReportPendingApproval) {
                    return;
                  }
                  goToTalkResults(REPORT_LINK.REPORT, reportAccess.accessKey);
                  onResultsClick();
                }}
                data-icom={'btn-completed-survey-card-results'}
              >
                {t('results')}
              </LinkButton>
            </ReportPendingApprovalTooltip>
          )}
          {!hideDetail && (
            <LinkButton onClick={() => surveyDetailPath.toFunc(survey.id)} data-cy={'btn-survey-detail'}>
              {t('detail')}
            </LinkButton>
          )}
        </>
      }
    />
  );
};

const Rate = styled.div`
  padding-bottom: ${theme.spacing.f};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const Participants = styled.span`
  svg {
    padding-right: ${theme.spacing.c};
    > path {
      fill: ${theme.colors.actionPrimary.default};
    }
  }
`;
